import {
    type AnnualCashFlow,
    RetirementAccountType,
} from '@fruitful/financial-calculators';
import { formatDollars } from '@fruitful/fruitils';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useMemo, useRef, useState } from 'react';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Checkbox,
    XStack,
    YStack,
} from 'smoothie';

import { getCalendarYear } from '../utils/retirementCalculatorUtils';

interface AnnualCashFlowDataTableProps {
    annualCashFlows: AnnualCashFlow[];
}

interface TableRow {
    year: string;
    primaryMemberAge: number;
    partnerMemberAge?: number;
    primaryMemberYearType: string;
    partnerMemberYearType?: string;
    totalStartingAssets: number;
    totalContributions: number;
    totalTaxes: number;
    totalGrowth: number;
    totalDistributions: number;
    totalDistributionTaxes: number;
    totalSocialSecurityTaxes: number;
    totalEndingAssets: number;
    primaryMemberSocialSecurityBenefit: number;
    partnerMemberSocialSecurityBenefit?: number;
    retirementExpenses: number;
    retirementDrawdown: number;
    retirementHealthcareCosts: number;
    longTermCareCosts: number;
    totalRetirementExpenses: number;
    effectiveTaxRate: number;
    insufficientFunds: boolean;
    // Account-specific columns will be added dynamically
    [key: string]: string | number | boolean | undefined;
}

export const AnnualCashFlowDataTable = ({
    annualCashFlows,
}: AnnualCashFlowDataTableProps) => {
    const dt = useRef<DataTable<TableRow[]>>(null);
    const [showAccountLevelData, setShowAccountLevelData] =
        useState<boolean>(false);

    const exportCSV = () => {
        if (dt.current != null) {
            dt.current.exportCSV();
        }
    };

    // Transform data for the table
    const tableData = useMemo((): TableRow[] => {
        if (annualCashFlows.length === 0) {
            return [];
        }

        const accountIds =
            annualCashFlows[annualCashFlows.length - 1]?.endingAssets.map(
                (asset) => {
                    return asset.id;
                },
            ) ?? [];

        return annualCashFlows.map((cashFlow) => {
            const row: TableRow = {
                year: `Year ${cashFlow.year} (${getCalendarYear(cashFlow.year)})`,
                primaryMemberAge: cashFlow.primaryMemberAge,
                partnerMemberAge: cashFlow.partnerMemberAge,
                primaryMemberYearType: cashFlow.primaryMemberYearType,
                partnerMemberYearType: cashFlow.partnerMemberYearType,
                totalStartingAssets: cashFlow.startingAssets.reduce(
                    (sum, asset) => sum + asset.balance,
                    0,
                ),
                totalContributions: cashFlow.contributions.reduce(
                    (sum, contribution) => sum + contribution.totalContribution,
                    0,
                ),
                totalGrowth: cashFlow.growth.reduce(
                    (sum, g) => sum + (g.growth ?? 0),
                    0,
                ),
                totalDistributions: cashFlow.distributions.reduce(
                    (sum, d) => sum + d.grossDistributionAmount,
                    0,
                ),
                totalDistributionTaxes: cashFlow.distributions.reduce(
                    (sum, d) => sum + d.taxAmount,
                    0,
                ),
                totalSocialSecurityTaxes: cashFlow.totalSocialSecurityTax,
                totalTaxes:
                    cashFlow.distributions.reduce(
                        (sum, d) => sum + d.taxAmount,
                        0,
                    ) + cashFlow.totalSocialSecurityTax,
                totalEndingAssets: cashFlow.endingAssets.reduce(
                    (sum, asset) => sum + asset.balance,
                    0,
                ),
                primaryMemberSocialSecurityBenefit:
                    cashFlow.primaryMemberSocialSecurityBenefit,
                partnerMemberSocialSecurityBenefit:
                    cashFlow.partnerMemberSocialSecurityBenefit,
                retirementExpenses: cashFlow.retirementExpenses,
                retirementDrawdown: cashFlow.retirementDrawdown,
                retirementHealthcareCosts: cashFlow.retirementHealthcareCosts,
                longTermCareCosts: cashFlow.longTermCareCosts,
                totalRetirementExpenses: cashFlow.totalRetirementExpenses,
                effectiveTaxRate: cashFlow.effectiveTaxRate,
                insufficientFunds: cashFlow.insufficientFunds,
            };

            // Add account-specific columns in specific order
            accountIds.forEach((accountId) => {
                // Starting Assets
                const startingAsset = cashFlow.startingAssets.find(
                    (asset) => asset.id === accountId,
                );
                row[`${accountId} - Starting Balance`] =
                    startingAsset?.balance ?? 0;

                // Get contribution data for this account
                const contribution = cashFlow.contributions.find(
                    (c) => c.account.id === accountId,
                );

                // Check if this is a 401k account type
                const is401kAccount =
                    startingAsset?.type ===
                        RetirementAccountType.EMPLOYER_401K ||
                    startingAsset?.type === RetirementAccountType.ROTH_401K;

                if (is401kAccount) {
                    // Employee Contributions (only for 401k accounts)
                    row[`${accountId} - Employee Contribution`] =
                        contribution?.employeeContribution ?? 0;

                    // Employer Contributions (only for 401k accounts)
                    row[`${accountId} - Employer Contribution`] =
                        contribution?.employerContribution ?? 0;
                }

                // Total Contributions (always included)
                row[`${accountId} - Total Contribution`] =
                    contribution?.totalContribution ?? 0;

                // Growth
                const growth = cashFlow.growth.find(
                    (g) => g.account.id === accountId,
                );
                row[`${accountId} - Growth`] = growth?.growth ?? 0;

                // Distributions
                const distribution = cashFlow.distributions.find(
                    (d) => d.account.id === accountId,
                );
                row[`${accountId} - Distribution`] =
                    distribution?.grossDistributionAmount ?? 0;

                // Taxes
                row[`${accountId} - Tax`] = distribution?.taxAmount ?? 0;

                // Ending Assets
                const endingAsset = cashFlow.endingAssets.find(
                    (asset) => asset.id === accountId,
                );
                row[`${accountId} - Ending Balance`] =
                    endingAsset?.balance ?? 0;
            });

            return row;
        });
    }, [annualCashFlows]);

    // Generate columns dynamically based on the data
    const columns = useMemo(() => {
        if (tableData.length === 0) {
            return [];
        }

        // Check if there are multiple members by looking at the first row's partnerMemberAge
        const isJointMembership = tableData[0]?.partnerMemberAge !== undefined;

        const baseColumns = [
            {
                field: 'year',
                header: 'Year',
                style: { minWidth: '80px' },
            },
            {
                field: 'primaryMemberAge',
                header: 'Primary Member Age',
                style: { minWidth: '120px' },
            },
            ...(isJointMembership
                ? [
                      {
                          field: 'partnerMemberAge',
                          header: 'Partner Member Age',
                          style: { minWidth: '120px' },
                      },
                  ]
                : []),
            {
                field: 'primaryMemberYearType',
                header: 'Primary Member Year Type',
                style: { minWidth: '140px' },
            },
            ...(isJointMembership
                ? [
                      {
                          field: 'partnerMemberYearType',
                          header: 'Partner Member Year Type',
                          style: { minWidth: '140px' },
                      },
                  ]
                : []),
            {
                field: 'totalStartingAssets',
                header: 'Total Starting Assets',
                style: { minWidth: '120px' },
                body: (row: TableRow) => formatDollars(row.totalStartingAssets),
            },
            {
                field: 'totalContributions',
                header: 'Total Contributions',
                style: { minWidth: '120px' },
                body: (row: TableRow) => formatDollars(row.totalContributions),
            },
            {
                field: 'totalGrowth',
                header: 'Total Growth',
                style: { minWidth: '120px' },
                body: (row: TableRow) => formatDollars(row.totalGrowth),
            },
            {
                field: 'totalDistributions',
                header: 'Total Distributions',
                style: { minWidth: '120px' },
                body: (row: TableRow) => formatDollars(row.totalDistributions),
            },
            {
                field: 'totalDistributionTaxes',
                header: 'Distribution Taxes',
                style: { minWidth: '130px' },
                body: (row: TableRow) =>
                    formatDollars(row.totalDistributionTaxes),
            },
            {
                field: 'totalSocialSecurityTaxes',
                header: 'SS Taxes',
                style: { minWidth: '100px' },
                body: (row: TableRow) =>
                    formatDollars(row.totalSocialSecurityTaxes),
            },
            {
                field: 'totalTaxes',
                header: 'Total Taxes',
                style: { minWidth: '120px' },
                body: (row: TableRow) => formatDollars(row.totalTaxes),
            },
            {
                field: 'totalEndingAssets',
                header: 'Total Ending Assets',
                style: { minWidth: '120px' },
                body: (row: TableRow) => formatDollars(row.totalEndingAssets),
            },
            {
                field: 'primaryMemberSocialSecurityBenefit',
                header: 'Primary Member SS Benefit',
                style: { minWidth: '140px' },
                body: (row: TableRow) =>
                    formatDollars(row.primaryMemberSocialSecurityBenefit),
            },
            ...(isJointMembership
                ? [
                      {
                          field: 'partnerMemberSocialSecurityBenefit',
                          header: 'Partner Member SS Benefit',
                          style: { minWidth: '140px' },
                          body: (row: TableRow) =>
                              formatDollars(
                                  row.partnerMemberSocialSecurityBenefit ?? 0,
                              ),
                      },
                  ]
                : []),
            {
                field: 'retirementExpenses',
                header: 'Retirement Expenses',
                style: { minWidth: '120px' },
                body: (row: TableRow) => formatDollars(row.retirementExpenses),
            },
            {
                field: 'retirementHealthcareCosts',
                header: 'Retirement Healthcare Costs',
                style: { minWidth: '160px' },
                body: (row: TableRow) =>
                    formatDollars(row.retirementHealthcareCosts),
            },
            {
                field: 'longTermCareCosts',
                header: 'Long Term Care Costs',
                style: { minWidth: '140px' },
                body: (row: TableRow) => formatDollars(row.longTermCareCosts),
            },
            {
                field: 'totalRetirementExpenses',
                header: 'Total Retirement Expenses',
                style: { minWidth: '160px' },
                body: (row: TableRow) =>
                    formatDollars(row.totalRetirementExpenses),
            },
            {
                field: 'effectiveTaxRate',
                header: 'Effective Tax Rate',
                style: { minWidth: '120px' },
                body: (row: TableRow) =>
                    `${(row.effectiveTaxRate * 100).toFixed(2)}%`,
            },
            {
                field: 'retirementDrawdown',
                header: 'Retirement Drawdown',
                style: { minWidth: '120px' },
                body: (row: TableRow) => formatDollars(row.retirementDrawdown),
            },
            {
                field: 'insufficientFunds',
                header: 'Insufficient Funds',
                style: { minWidth: '100px' },
                body: (row: TableRow) => (row.insufficientFunds ? 'Yes' : 'No'),
            },
        ];

        // Add account-specific columns only if checkbox is checked
        if (showAccountLevelData && tableData.length > 0) {
            const firstRow = tableData[0];
            if (firstRow) {
                const accountColumns = Object.keys(firstRow)
                    .filter((key) => key.includes(' - ')) // TODO - Improve logic here to more precisely target account columns.
                    .map((key) => ({
                        field: key,
                        header: key,
                        style: { minWidth: '120px' },
                        body: (row: TableRow) => {
                            const value = row[key];
                            if (typeof value === 'number') {
                                return formatDollars(value);
                            }
                            return value;
                        },
                    }));

                return [...baseColumns, ...accountColumns];
            }
        }

        return baseColumns;
    }, [tableData, showAccountLevelData]);

    if (annualCashFlows.length === 0) {
        return (
            <YStack padding={16}>
                <BodyMediumRegular>
                    No annual cash flow data available.
                </BodyMediumRegular>
            </YStack>
        );
    }

    return (
        <YStack width="100%" gap={24}>
            <XStack justifyContent="space-between" alignItems="center">
                <XStack alignItems="center" gap={12}>
                    <Checkbox
                        id="show-account-level-data"
                        checked={showAccountLevelData}
                        onCheckedChange={(isChecked) => {
                            setShowAccountLevelData(isChecked as boolean);
                        }}
                    />
                    <BodyMediumRegular>
                        Show Account-Level Data
                    </BodyMediumRegular>
                </XStack>
                <Button
                    alignSelf="flex-end"
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.SMALL}
                    text="Export to CSV"
                    onPress={exportCSV}
                    disabled={tableData.length === 0}
                    width="auto"
                />
            </XStack>

            <DataTable
                ref={dt}
                value={tableData}
                size="small"
                tableStyle={{ width: '100%', fontSize: 13 }}
                scrollable
                scrollHeight="60vh"
                sortField="year"
                sortOrder={1}
                showGridlines
                stripedRows
            >
                {columns.map((col) => (
                    <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        style={col.style}
                        body={col.body}
                    />
                ))}
            </DataTable>
        </YStack>
    );
};
